// JOBE-0164: PromptOpsFleetPage — W6 Fleet surface.
//
// Route: /promptops/fleet
// Gated on the promptops.route feature flag.
//
// Sections (JOBE-0164.1–.5):
//   .1 Identity head — agentCount, deptCount, prompt-roster tag
//   .2 FleetQualityStrip — 5 KPI tiles
//   .3 FailureClassIndex — ranked failure classes with cohort-fix
//   .4 AgentRankingTable — sortable per-agent ranking
//   .5 RecentCorrectionsFeed + RecentForgetFeed — 2-column recent-events grid
//
// JOBE-0167.6 fills the pre-reserved right column of the recent-events grid
// with <RecentForgetFeed />.

import { useEffect, useState } from 'react';
import { PromptOpsSubNav } from '../components/PromptOpsSubNav/PromptOpsSubNav.jsx';
import { FleetQualityStrip } from './FleetQualityStrip.jsx';
import { FailureClassIndex } from './FailureClassIndex.jsx';
import { AgentRankingTable } from './AgentRankingTable.jsx';
import { RecentCorrectionsFeed } from './RecentCorrectionsFeed.jsx';
import { RecentForgetFeed } from './RecentForgetFeed.jsx';
import { useFlagStore } from '../lib/useFlagStore.js';
import { flaggedFetch } from '../lib/flags.js';
import '../mock-wcc.js'; // registers fixtures with FIXTURES map

// ---- W6 identity head ----
//
// Derives the "prompts v{N}" tag from max(agents.promptVersion).
// Rendered as .w6-id-head for selector targeting in tests.
function W6IdentityHead({ fleet }) {
  const agentCount = fleet?.agentCount ?? 0;
  const deptCount  = fleet?.deptCount  ?? 0;
  const maxVersion = (fleet?.agents ?? []).reduce(
    (max, a) => Math.max(max, a.promptVersion ?? 0),
    0
  );

  return (
    <div className="w6-id-head" data-testid="w6-id-head">
      <span className="w6-id-head__agents">{agentCount} agents</span>
      <span className="w6-id-head__sep"> · </span>
      <span className="w6-id-head__depts">{deptCount} departments</span>
      {maxVersion > 0 && (
        <>
          <span className="w6-id-head__sep"> · </span>
          <span className="w6-id-head__prompt-tag">prompts v{maxVersion}</span>
        </>
      )}
    </div>
  );
}

// ---- PromptOpsFleetPage ----
export function PromptOpsFleetPage() {
  const isFlagOn  = useFlagStore((s) => s.isFlagOn);
  const overrides = useFlagStore((s) => s.overrides);
  const routeEnabled = isFlagOn('promptops.route');

  const [fleet, setFleet] = useState(null);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    setLoadState('loading');
    const fx = flaggedFetch('promptops.mock.fleet', overrides);
    if (fx !== undefined) {
      setFleet(fx);
      setLoadState('loaded');
      return;
    }
    // Real endpoint — not yet implemented; surface the error state so operators
    // can navigate elsewhere. TODO(JOBE-0164-followup): wire /api/promptops/fleet.
    let cancelled = false;
    fetch('/api/promptops/fleet')
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!cancelled) {
          setFleet(body);
          setLoadState(body ? 'loaded' : 'error');
        }
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });
    return () => { cancelled = true; };
  }, [overrides]);

  if (!routeEnabled) {
    return (
      <div
        className="promptops-fleet-page-disabled"
        data-testid="promptops-fleet-page-disabled"
      >
        <p>PromptOps Fleet is not enabled. Enable the <code>promptops.route</code> flag.</p>
      </div>
    );
  }

  if (loadState === 'error' || (loadState === 'loaded' && !fleet)) {
    return (
      <>
        <PromptOpsSubNav agentName={null} />
        <div className="promptops-fleet-page" data-testid="promptops-fleet-page">
          <div className="promptops-fleet-page__crumb">PromptOps / Fleet</div>
          <p className="promptops-fleet-page__error">Failed to load fleet data.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PromptOpsSubNav agentName={null} />
      <div className="promptops-fleet-page" data-testid="promptops-fleet-page">
        <div className="promptops-fleet-page__crumb">PromptOps / Fleet</div>

        {loadState === 'loaded' && fleet && (
          <>
            <W6IdentityHead fleet={fleet} />

            <FleetQualityStrip fleet={fleet} />

            <FailureClassIndex fleet={fleet} />

            <AgentRankingTable fleet={fleet} />

            {/* JOBE-0164.5 / JOBE-0167.6: two-column recent-events grid.
                Left = corrections (JOBE-0164.5), right = forget feed (JOBE-0167.6). */}
            <div className="recent-events-grid">
              <RecentCorrectionsFeed
                corrections={fleet.recentCorrections ?? []}
                limit={10}
              />
              <RecentForgetFeed limit={10} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default PromptOpsFleetPage;
